import { Trans, useTranslation } from 'next-i18next'
// Dependencies.
import styled, { css } from 'styled-components'
import { useEffect, useMemo, useState } from 'react'

import { Select } from '../../Inputs/Select'
import { Input } from '../../Inputs/Input'
import { generateSelectControlWithIcon } from '../../SelectControlWithIcon'
import { SectionField } from '../../Inputs/SectionField'
import { FormField } from '../../Inputs/FormField'
import {
  icons,
  stringFromQuery,
  fontWeights,
  forScreensGreaterThanMobileWidth,
  colors,
} from '../../../utils'
import { H5 } from '../../H5'
import { P } from '../../P'
import { EventHookInterface, Option } from '../../../types'
import { Hyperlink } from '../../Hyperlink'
import { MeetingLinkInput } from './MeetingLinkInput'
import { ZoomMeetingLinkInput } from './ZoomMeetingLinkInput'

// Div.
const Div = styled.div`
  text-align: left;

  .zoom {
    align-items: center;
    border: 2px solid #f1f2f5;
    border-radius: 6px;
    box-sizing: border-box;
    display: flex;
    height: 45px;
    justify-content: flex-start;
    padding: 10px;
  }

  .zoom img {
    height: 32px;
    margin-right: 10px;
    width: 32px;
  }

  .information {
    color: #2b2b2b;
    margin-top: 15px;
    opacity: 0.5;
    font-size: 14px;
    letter-spacing: -0.5px;
    line-height: 140%;
  }

  .inputError {
    border-color: ${colors.red};
    opacity: 0.6;
  }

  .pasteInput {
  }

  .formField {
    width: 100%;
    ${forScreensGreaterThanMobileWidth(css`
      width: 325px;
    `)}
  }

  .selectInput {
    width: 100%;
  }

  .zoomReAuthLink {
    color: ${colors.helperTextGray};
    margin-top: 8px;
    a {
      font-size: 14px;
      color: ${colors.helperTextGray};
      text-decoration: underline;
      font-weight: ${fontWeights.semiBold};
    }
  }
`

const PasteInput = styled(Input)`
  margin-top: 20px;
`

// Props.
interface Props {
  eventHook: EventHookInterface
}

// Conferencing Field.
export const ConferencingField: React.FC<Props> = (props) => {
  const { t } = useTranslation('common')

  const { eventHook } = props
  const {
    isEditable,
    value,
    showZoomReAuthLink,
    updateShowZoomAuthoriseModal,
  } = eventHook

  // State
  const [showPasteField, setShowPasteField] = useState(false)
  const [linkType, setLinkType] = useState('')

  // Add icon
  const ValueContainer = useMemo(() => {
    return generateSelectControlWithIcon(icons.fadedPeople)
  }, [])

  // Host Options.
  const ZOOM = { label: t('Zoom Meeting', 'Zoom Meeting'), value: '0' }
  const CUSTOM_LINK = { label: t('Custom link', 'Custom link'), value: '1' }
  const options = [ZOOM, CUSTOM_LINK]

  // Value
  const fieldValue = options.find((option) => {
    return option.value === eventHook.value.video_host_type.toString()
  })

  // On Host Type Change.
  const onHostTypeChange = ({ value }: Option) => {
    if (value == '1') {
      setShowPasteField(true)
      setHostType(parseInt(value))
    } else {
      setShowPasteField(false)
      setHostType(parseInt(value))
    }
  }

  // Validate link
  const validateLink = (fieldValue: string) => {
    if (
      fieldValue.includes('meet.google.com') ||
      fieldValue.includes('zoom.us') ||
      fieldValue.includes('webinarjam.com')
    ) {
      if (fieldValue.includes('meet.google.com')) {
        setLinkType('google')
      }
      if (fieldValue.includes('zoom.us')) {
        setLinkType('zoom')
      }
      if (fieldValue.includes('webinarjam.com')) {
        setLinkType('webinarjam')
      }
      if (fieldValue.includes('webinarjam.com')) {
        setLinkType('webinarjam')
      }
    } else {
      setLinkType('invalid')
    }
  }

  useEffect(() => {
    if (eventHook.value.video_host_type === 1) {
      setShowPasteField(true)
    }
  }, [eventHook.value.video_host_type])

  const setHostType = (hostTypeValue: number) => {
    // console.log('host type before changed:', Number.isInteger(hostTypeValue), 'and:', Number.isInteger(eventHook.value.video_host_type))
    eventHook.updateFieldsWithPartial({ video_host_type: hostTypeValue })
    // console.log('host type after changed:', Number.isInteger(hostTypeValue), 'and:', Number.isInteger(eventHook.value.video_host_type))
  }
  const setVideoLink = (event) => {
    validateLink(event.target.value)
    eventHook.updateFieldsWithPartial({ video_url: event.target.value })
  }

  const showZoomAuthModal = (event: React.MouseEvent<HTMLAnchorElement>) => {
    updateShowZoomAuthoriseModal(true)
  }

  return (
    <SectionField
      header={isEditable ? 'Video call setup' : 'Meeting info'}
      subheader={
        isEditable
          ? t(
              "We've teamed up with Zoom to generate a Zoom meeting link automatically, all you need to do is share it. Alternatively, you can also paste in a Google Hangouts link via the manual option.",
              "We've teamed up with Zoom to generate a Zoom meeting link automatically, all you need to do is share it. Alternatively, you can also paste in a Google Hangouts link via the manual option.",
            )
          : null
      }>
      <Div>
        {/* <FormField title={isEditable ? "Hosted on *" : "Meeting link:"}> */}
        {isEditable ? (
          <div>
            <FormField
              className="formField"
              required
              title={
                isEditable
                  ? t('Hosted on', 'Hosted on')
                  : t('Meeting link:', 'Meeting link:')
              }>
              <Select
                components={{ ValueContainer }}
                className=""
                instanceId="hostField"
                onChange={onHostTypeChange}
                options={options}
                placeholder={t(`Select host type`, `Select host type`)}
                value={fieldValue}
                errors={eventHook.error.video_host_type}
                disabled={!!value.id}
              />
            </FormField>

            {/* {eventHook.value.video_host_type} */}

            {showPasteField ? (
              <PasteInput
                placeholder={t(`Paste link`, `Paste link`)}
                autoCapitalize="none"
                // onChange={validateLink}
                onChange={setVideoLink}
                className={`pasteInput ${
                  eventHook.error.video_url &&
                  eventHook.error.video_url.length > 0
                    ? 'inputError'
                    : ''
                }`}
                value={eventHook.value.video_url || ''}
                disabled={!!value.id}
              />
            ) : null}
            {eventHook.error.video_url && eventHook.error.video_url.length > 0 && (
              <div className="errorContainer">
                {eventHook.error.video_url.map((error, index) => (
                  <P
                    key={index}
                    className="detail"
                    style={{ color: `${colors.red}` }}>
                    {error}
                  </P>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            {/* {value.video_url.length < 65 && <MeetingLinkInput eventHook={eventHook} />} */}
            {/* {value.video_url.length > 65 && <ZoomMeetingLinkInput eventHook={eventHook} />} */}
            <MeetingLinkInput eventHook={eventHook} />
            {value.video_host_type === 0 ? (
              <P className="information">
                {t(`Hosted on Zoom`, `Hosted on Zoom`)}
              </P>
            ) : value.video_host_type === 1 && linkType === 'zoom' ? (
              <P className="information">
                {t(`Hosted on Zoom`, `Hosted on Zoom`)}
              </P>
            ) : linkType === 'google' ? (
              <P className="information">
                {t(`ConferencingField1222281702`, `Hosted on Google Meet`)}
              </P>
            ) : null}
          </div>
        )}

        {value.video_host_type === 1 && linkType === 'zoom' ? (
          <P className="information">
            {t(
              `ConferencingField1502563419`,
              `Free users on Zoom are capped at 40 minutes.`,
            )}{' '}
          </P>
        ) : null}
        {value.video_host_type === 1 && linkType === 'google' ? (
          <P className="information">
            {t(
              `ConferencingField1970331034`,
              `Free users on Google Meet are capped at 1 hour after September 30th.`,
            )}
          </P>
        ) : null}
        {isEditable && showZoomReAuthLink ? (
          <P className="detail zoomReAuthLink">
            <Trans i18nKey="ConferencingField-20220106165942515133414">
              <a className="" onClick={showZoomAuthModal}>
                Click here
              </a>{' '}
              to re-authorize your zoom account
            </Trans>
          </P>
        ) : null}
        {isEditable && !showZoomReAuthLink ? (
          <P className="information">
            {showPasteField
              ? t(
                  'Only Zoom, Google, and WebinarJam Meet links are available at this time.',
                  'Only Zoom, Google, and WebinarJam Meet links are available at this time.',
                )
              : t(
                  'Zoom is our partner (More platforms to come down the track). Your Zoom link will be generated for you upon the completion of publishing this event.',
                  'Zoom is our partner (More platforms to come down the track). Your Zoom link will be generated for you upon the completion of publishing this event.',
                )}
          </P>
        ) : null}
        {/* </FormField> */}
      </Div>
    </SectionField>
  )
}
